import { supabase } from '../lib/supabase';
import { Platform } from 'react-native';
import * as FileSystemLegacy from 'expo-file-system/legacy';
import profilePhotoCacheService from './profilePhotoCacheService';

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

// Network/IO timeouts so a stalled request can never spin the UI forever.
const SESSION_TIMEOUT_MS = 10000;
const FILE_PREP_TIMEOUT_MS = 30000;
const STORAGE_UPLOAD_TIMEOUT_MS = 45000;
const PROFILE_UPDATE_TIMEOUT_MS = 15000;

export class ImageUploadService {
  /**
   * Reject a promise if it does not settle within `ms`. Prevents the
   * profile-photo upload from hanging indefinitely (infinite spinner).
   */
  private static withTimeout<T>(promise: PromiseLike<T>, ms: number, label: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error(`${label} timed out after ${ms}ms`));
      }, ms);
      Promise.resolve(promise).then(
        (value) => { clearTimeout(timer); resolve(value); },
        (err) => { clearTimeout(timer); reject(err); }
      );
    });
  }

  /**
   * Upload profile picture with optimized settings
   */
  static async uploadProfilePicture(userId: string, imageUri: string): Promise<{ url: string | null; error: any }> {
    try {
      console.log('📤 Starting profile picture upload...', { userId, imageUri });

      // Validate inputs
      if (!userId || !imageUri) {
        const error = new Error('Missing required parameters: userId or imageUri');
        console.error('❌ Upload validation failed:', error.message);
        return { url: null, error };
      }

      // CRITICAL: refresh the Supabase auth state before uploading. On the
      // very first photo upload right after signup, the storage client may
      // hold a stale (pre-signin) auth header and the upload request can
      // hang indefinitely waiting for an auth challenge. Forcing a session
      // read here repopulates the internal auth state cleanly.
      try {
        const { data: sessionData } = await this.withTimeout(
          (async () => await supabase.auth.getSession())(),
          SESSION_TIMEOUT_MS,
          'Session check'
        );
        if (!sessionData?.session) {
          const error = new Error('No active session for upload');
          console.error('❌ Upload aborted: no active session');
          return { url: null, error };
        }
      } catch (sessionError) {
        console.warn('⚠️ Session check before upload failed (continuing):', sessionError);
      }

      // Create optimized file path with timestamp to avoid collisions
      const timestamp = Date.now();
      const fileName = `profile_${userId}_${timestamp}.jpg`;
      const filePath = `avatars/${fileName}`;

      console.log('📁 Upload destination:', filePath);

      // Prepare file for upload with optimization.
      // Wrapped in a hard timeout (not just the internal AbortController) because
      // on Android `AbortController.abort()` does not reliably cancel a stalled
      // fetch/arrayBuffer read of a content:// image URI, which would otherwise
      // leave the onboarding photo step spinning forever. The outer timeout is
      // slightly longer than the internal one so the abort path runs first, but
      // termination is guaranteed either way.
      let fileData: { file: any; contentType: string } | null = null;
      try {
        fileData = await this.withTimeout(
          this.prepareFileForUpload(imageUri, fileName),
          FILE_PREP_TIMEOUT_MS + 5000,
          'File preparation'
        );
      } catch (prepError) {
        console.error('❌ File preparation timed out or failed:', prepError);
        return { url: null, error: prepError };
      }
      if (!fileData) {
        const error = new Error('Failed to prepare file for upload');
        console.error('❌ File preparation failed');
        return { url: null, error };
      }

      console.log('📦 File prepared:', {
        name: fileName,
        type: fileData.contentType
      });

      // Upload to Supabase Storage with avatar-optimized settings.
      // Wrapped in a timeout so a stalled network request fails cleanly
      // instead of leaving the UI stuck on an infinite loading spinner.
      const { data, error: uploadError } = await this.withTimeout(
        (async () =>
          await supabase.storage
            .from('avatars')
            .upload(filePath, fileData.file, {
              cacheControl: '86400', // 24 hours cache for profile pictures
              upsert: false, // Always create new to avoid cache issues
              contentType: fileData.contentType,
            }))(),
        STORAGE_UPLOAD_TIMEOUT_MS,
        'Storage upload'
      );

      if (uploadError) {
        console.error('❌ Supabase upload failed:', uploadError);
        return { url: null, error: uploadError };
      }

      console.log('✅ File uploaded successfully:', data);

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      if (!urlData?.publicUrl) {
        const error = new Error('Failed to get public URL');
        console.error('❌ Public URL generation failed');
        return { url: null, error };
      }

      console.log('🔗 Public URL generated:', urlData.publicUrl);

      // Update user profile with new avatar URL
      const updateResult = await this.updateUserProfile(userId, urlData.publicUrl);
      if (updateResult.error) {
        console.error('❌ Profile update failed:', updateResult.error);
        // Don't return error here - file was uploaded successfully
        console.log('⚠️ File uploaded but profile update failed');
      } else {
        // Update profile photo cache with new URL. Fire-and-forget so local
        // cache I/O can never block/hang the upload flow's loading spinner.
        profilePhotoCacheService
          .updateProfilePhoto(userId, urlData.publicUrl)
          .then(() => console.log('📸 Profile photo cache updated'))
          .catch((cacheError) =>
            console.warn('⚠️ Profile photo cache update failed:', cacheError)
          );
      }

      console.log('🎉 Profile picture upload completed successfully');
      return { url: urlData.publicUrl, error: null };

    } catch (error) {
      console.error('❌ Upload service error:', error);
      return { url: null, error };
    }
  }

  /**
   * Prepare file for upload based on platform
   */
  private static async prepareFileForUpload(imageUri: string, fileName: string): Promise<{
    file: any;
    contentType: string;
  } | null> {
    try {
      if (Platform.OS === 'web') {
        // Web platform - handle different URI types.
        console.log('🌐 Web upload: Converting to blob...');

        // Abort the fetch if reading the local file stalls, so we never hang.
        const controller = new AbortController();
        const abortTimer = setTimeout(() => controller.abort(), FILE_PREP_TIMEOUT_MS);
        try {
          // data:, blob:, and regular URLs are all handled the same way.
          const response = await fetch(imageUri, { signal: controller.signal });
          const blob = await response.blob();

          if (!blob || blob.size === 0) {
            console.error('❌ Prepared file is empty (web blob)');
            return null;
          }

          return {
            file: blob,
            contentType: blob.type || 'image/jpeg',
          };
        } finally {
          clearTimeout(abortTimer);
        }
      }

      // React Native platform — read the picked file as base64 through
      // expo-file-system and decode it to bytes. fetch(uri).arrayBuffer() on a
      // local file:// / ph:// URI is unreliable on iOS in production (Hermes):
      // it can hang indefinitely, leaving the avatar spinner stuck on
      // "Enviando foto…". Reading through the filesystem API is deterministic.
      console.log('📱 Mobile upload: Reading file via expo-file-system...');
      const base64 = await FileSystemLegacy.readAsStringAsync(imageUri, { encoding: 'base64' });

      if (!base64 || base64.length === 0) {
        console.error('❌ Prepared file is empty (native base64)');
        return null;
      }

      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }

      if (bytes.byteLength === 0) {
        console.error('❌ Prepared file is empty after decode (native)');
        return null;
      }

      return {
        file: bytes,
        contentType: 'image/jpeg',
      };
    } catch (error) {
      console.error('❌ Error preparing file for upload:', error);
      return null;
    }
  }

  /**
   * Get file extension from URI
   */
  private static getFileExtension(uri: string): string {
    // Try to extract extension from URI
    const match = uri.match(/\.([^.?]+)(\?|$)/);
    if (match) {
      return match[1].toLowerCase();
    }

    // Default to jpg for images
    return 'jpg';
  }

  /**
   * Clean up old avatar files
   */
  static async cleanupOldAvatar(avatarUrl: string): Promise<void> {
    try {
      if (!avatarUrl) return;

      // Extract filename from URL
      const urlParts = avatarUrl.split('/');
      const fileNameWithParams = urlParts[urlParts.length - 1];
      const fileName = fileNameWithParams?.split('?')[0];

      if (!fileName || !fileName.startsWith('profile_')) {
        console.log('⚠️ Invalid avatar filename, skipping cleanup:', fileName);
        return;
      }

      console.log('🗑️ Cleaning up old avatar:', fileName);

      const { error } = await supabase.storage
        .from('avatars')
        .remove([fileName]);

      if (error) {
        console.warn('⚠️ Failed to cleanup old avatar:', error.message);
      } else {
        console.log('✅ Old avatar cleaned up successfully');
      }
    } catch (error) {
      console.warn('⚠️ Exception during avatar cleanup:', error);
    }
  }

  /**
   * Validate image file
   */
  static validateImageFile(uri: string): { isValid: boolean; error?: string } {
    try {
      // Check if URI exists
      if (!uri || uri.trim().length === 0) {
        return { isValid: false, error: 'URI não encontrada' };
      }

      // For web, check if it's a valid data/blob URL
      if (Platform.OS === 'web') {
        if (!uri.startsWith('data:') && !uri.startsWith('blob:') && !uri.startsWith('http')) {
          return { isValid: false, error: 'Formato de arquivo inválido' };
        }
      }

      return { isValid: true };
    } catch (error) {
      return { isValid: false, error: 'Erro ao validar arquivo' };
    }
  }

  /**
   * Update user profile in Supabase
   */
  private static async updateUserProfile(userId: string, avatarUrl: string): Promise<{ error: any }> {
    try {
      console.log('🔄 Updating user profile with new avatar URL...');

      // Bounded so a stalled DB write can never hang the upload flow's spinner.
      const { error } = await this.withTimeout(
        (async () =>
          await supabase
            .from('profiles')
            .update({ avatar_url: avatarUrl })
            .eq('id', userId))(),
        PROFILE_UPDATE_TIMEOUT_MS,
        'Profile update'
      );

      if (error) {
        console.error('❌ Profile update error:', error);
        return { error };
      }

      console.log('✅ Profile updated successfully');
      return { error: null };
    } catch (error) {
      console.error('❌ Exception updating profile:', error);
      return { error };
    }
  }
}